import { ReactNode, useState } from 'react'

import { AnimatePresence, motion } from 'framer-motion'
import useMeasure from 'react-use-measure'
import {
  Text,
  Button,
  GreyGrey30,
  GreyGrey50,
  GreyGrey60,
  GreyGrey70,
  GreyGrey80,
  GreyWhite,
  IconEmail,
  IndigoIndigo60,
  Primary13Regular,
  Primary13SemiBold,
  Box,
  Flex
} from '@sequencehq/core-components'
import { UserAction } from 'Automations/components'
import { formatTimestamp } from 'Automations/helpers'
import { AgentReviewedEmail } from './types'

const rowBorder = `${GreyGrey30} 1px solid`

const crossFadeMotionProps = {
  initial: { opacity: 0 },
  animate: { opacity: 1 },
  exit: { opacity: 0 },
  transition: { duration: 0.2, ease: 'easeInOut' }
} as const

/**
 * Collapsible forwarded-email header shown at the top of an agent-review modal's
 * preview pane. Collapsed it reads as a one-line attribution ("Email forwarded
 * from …") with a "See more" affordance; expanded it reveals the sender, subject
 * and body of the original email as flat full-width rows. Shared by the
 * customer-info and invoice-update reviews.
 *
 * Intentionally not the shared `EmailPreview` (which the editor panel uses):
 * that renders as a bordered card with its own collapse behaviour, whereas
 * this header sits flush with the pane and collapses to an attribution line.
 */
export const SourceEmailHeader = ({
  source,
  onExpandedChange,
  'data-testid': dataTestId
}: {
  source: AgentReviewedEmail
  /** Notifies the parent on toggle, e.g. to dim the content behind. */
  onExpandedChange?: (expanded: boolean) => void
  'data-testid'?: string
}) => {
  const [isExpanded, setIsExpanded] = useState(false)
  const [contentRef, contentBounds] = useMeasure()

  const setExpanded = (expanded: boolean) => {
    setIsExpanded(expanded)
    onExpandedChange?.(expanded)
  }

  const childTestId = (suffix: string) =>
    dataTestId ? `${dataTestId}.${suffix}` : undefined

  return (
    <Box
      backgroundColor={GreyWhite}
      borderBottom={rowBorder}
      data-testid={dataTestId}
    >
      {/* Animate the container between the measured heights of the collapsed
          and expanded content while the contents cross-fade — the same
          approach as the shared EmailPreview. */}
      <motion.div
        initial={false}
        animate={{ height: contentBounds.height || 'auto' }}
        transition={{ duration: 0.2, ease: 'easeInOut' }}
        style={{ overflow: 'hidden' }}
      >
        <Box ref={contentRef} position="relative">
          <AnimatePresence initial={false} mode="popLayout">
            {isExpanded ? (
              <motion.div key="expanded" {...crossFadeMotionProps}>
                <ExpandedHeader
                  source={source}
                  onCollapse={() => setExpanded(false)}
                  childTestId={childTestId}
                />
              </motion.div>
            ) : (
              <motion.div key="collapsed" {...crossFadeMotionProps}>
                <CollapsedHeader
                  source={source}
                  onExpand={() => setExpanded(true)}
                  childTestId={childTestId}
                />
              </motion.div>
            )}
          </AnimatePresence>
        </Box>
      </motion.div>
    </Box>
  )
}

const CollapsedHeader = ({
  source,
  onExpand,
  childTestId
}: {
  source: AgentReviewedEmail
  onExpand: () => void
  childTestId: (suffix: string) => string | undefined
}) => (
  <Flex
    alignItems="center"
    justifyContent="space-between"
    gap="8px"
    paddingLeft="16px"
    paddingRight="8px"
    paddingY="12px"
    cursor="pointer"
    userSelect="none"
    onClick={onExpand}
    data-testid={childTestId('collapsed')}
  >
    <UserAction
      icon={<IconEmail />}
      label={
        <>
          Email forwarded from{' '}
          <Text as="span" {...Primary13SemiBold} color={GreyGrey70}>
            {source.fromEmail}
          </Text>
        </>
      }
      timestamp={source.receivedAt}
    />
    <ToggleLink data-testid={childTestId('seeMore')}>See more</ToggleLink>
  </Flex>
)

const ExpandedHeader = ({
  source,
  onCollapse,
  childTestId
}: {
  source: AgentReviewedEmail
  onCollapse: () => void
  childTestId: (suffix: string) => string | undefined
}) => (
  <Flex flexDirection="column" paddingX="12px">
    <Flex
      alignItems="center"
      gap="8px"
      paddingX="4px"
      paddingY="12px"
      borderBottom={rowBorder}
    >
      <Text {...Primary13Regular} color={GreyGrey50} flexShrink={0}>
        From
      </Text>
      <Text {...Primary13Regular} color={GreyGrey80} noOfLines={1}>
        {source.fromEmail}
      </Text>
      <Text
        {...Primary13Regular}
        color={GreyGrey60}
        flex="1"
        whiteSpace="nowrap"
      >
        {formatTimestamp(source.receivedAt)}
      </Text>
      <ToggleLink onClick={onCollapse} data-testid={childTestId('seeLess')}>
        See less
      </ToggleLink>
    </Flex>

    <Flex paddingX="4px" paddingY="12px" borderBottom={rowBorder}>
      <Text
        {...Primary13Regular}
        color={GreyGrey80}
        noOfLines={1}
        data-testid={childTestId('subject')}
      >
        {source.subject}
      </Text>
    </Flex>

    <Text
      {...Primary13Regular}
      lineHeight="20px"
      color={GreyGrey80}
      paddingX="4px"
      paddingY="12px"
      whiteSpace="pre-wrap"
      data-testid={childTestId('body')}
    >
      {source.excerpt}
    </Text>
  </Flex>
)

// The `unstyled` Button is the codebase's text-button idiom for this kind of
// trigger (see ExpandableCard's "Show more"); the indigo text styling matches
// the "View invoice" link in the agent-activity rows design.
const ToggleLink = ({
  children,
  onClick,
  'data-testid': dataTestId
}: {
  children: ReactNode
  /** Omitted on the collapsed row, where the whole row is the click target. */
  onClick?: () => void
  'data-testid'?: string
}) => (
  <Button
    variant="unstyled"
    color={IndigoIndigo60}
    style={{ flexShrink: 0, whiteSpace: 'nowrap' }}
    onClick={onClick}
    data-testid={dataTestId}
  >
    {children}
  </Button>
)
